import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { supabase } from "../../supabase";
import { getActiveBuyBox, publishBuyBoxRule, invalidateBuyBoxCache } from "../../services/buyBoxRules";
import { evaluateBuyBox, PRODUCT_TYPES } from "../../services/buyBoxEvaluator";
import useMsaList from "../../hooks/useMsaList";

// ─── BuyBoxManagerPanel ─────────────────────────────────────────────────────
// Settings sub-panel for editing the active buy-box rule. Follows the same
// layout convention as MsaManagerPanel (slate-800 card, red action button).
// Every save publishes a NEW version row (buy_box_versions.version = max+1)
// and updates app_settings.buy_box_current_version to point at it, so past
// grades stay attributable to the rule they were computed against.
//
// The "Re-derive all rows" action walks every deal + property whose
// buy_box_source = 'auto' and rewrites its verdict against the current
// rule. Manual overrides are left alone.

const EMPTY_DRAFT = {
  msas: [],
  product_types: [],
  sf_min: "",
  sf_max: "",
  cap_rate_min: "",
  year_built_min: "",
  clear_height_min: "",
};

function toRule(draft) {
  const num = (s) => (s === "" || s == null ? null : Number(s));
  return {
    msas: draft.msas || [],
    product_types: draft.product_types || [],
    sf_min: num(draft.sf_min),
    sf_max: num(draft.sf_max),
    cap_rate_min: num(draft.cap_rate_min),
    year_built_min: num(draft.year_built_min),
    clear_height_min: num(draft.clear_height_min),
  };
}

function fromRule(rule) {
  return {
    msas: rule?.msas || [],
    product_types: rule?.product_types || [],
    sf_min: rule?.sf_min ?? "",
    sf_max: rule?.sf_max ?? "",
    cap_rate_min: rule?.cap_rate_min ?? "",
    year_built_min: rule?.year_built_min ?? "",
    clear_height_min: rule?.clear_height_min ?? "",
  };
}

export default function BuyBoxManagerPanel() {
  const { accounts } = useMsal();
  const authorEmail = accounts?.[0]?.username || null;

  const { msas: msaList } = useMsaList();

  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [savedRule, setSavedRule] = useState(EMPTY_DRAFT);
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [rederiving, setRederiving] = useState(false);
  const [rederiveResult, setRederiveResult] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rederiveSummary, setRederiveSummary] = useState(null);

  useEffect(() => {
    (async () => {
      const { rule, version: v } = await getActiveBuyBox();
      const initial = fromRule(rule);
      setDraft(initial);
      setSavedRule(initial);
      setVersion(v);
      setLoading(false);
      previewCounts(toRule(initial));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = JSON.stringify(draft) !== JSON.stringify(savedRule);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function toggleInList(field, value) {
    setDraft(d => {
      const cur = new Set(d[field] || []);
      if (cur.has(value)) cur.delete(value); else cur.add(value);
      return { ...d, [field]: [...cur] };
    });
  }

  // Runs the draft rule against a fresh sample of the DB so the panel can show
  // "your current draft would grade X in-box, Y filtered, Z undecided". Called
  // on mount and whenever the user hits "Preview counts".
  async function previewCounts(rule) {
    try {
      // Pull only the columns the evaluator reads. 1000-row cap is fine here —
      // this is a sanity-check, not the final re-derive.
      const [dealsRes, propsRes] = await Promise.all([
        supabase.from("deals")
          .select("id, msas, market, product_type, square_footage, cap_rate, year_built, clear_height, buy_box_source")
          .limit(1000),
        supabase.from("properties")
          .select("id, msas, market, product_type, size_sf, building_sf, year_built, clear_height, buy_box_source")
          .limit(1000),
      ]);
      const deals = dealsRes.data || [];
      const props = propsRes.data || [];

      const gradeMany = (rows, mapper) => {
        const out = { in_box: 0, filtered: 0, undecided: 0, manual: 0 };
        rows.forEach(r => {
          if (r.buy_box_source === "manual") { out.manual++; return; }
          const v = evaluateBuyBox(rule, mapper(r)).verdict;
          out[v] = (out[v] || 0) + 1;
        });
        return out;
      };
      const dealCounts = gradeMany(deals, r => ({
        msas: r.msas, market: r.market, product_type: r.product_type,
        squareFeet: r.square_footage, capRate: r.cap_rate,
        year_built: r.year_built, clear_height: r.clear_height,
      }));
      const propCounts = gradeMany(props, r => ({
        msas: r.msas, market: r.market, product_type: r.product_type,
        size_sf: r.size_sf, building_sf: r.building_sf,
        year_built: r.year_built, clear_height: r.clear_height,
      }));
      setRederiveSummary({ deals: dealCounts, properties: propCounts });
    } catch (e) {
      console.warn("preview counts failed:", e.message);
    }
  }

  async function handleSave() {
    setSaving(true);
    const rule = toRule(draft);
    const result = await publishBuyBoxRule(rule, {
      changedBy: authorEmail,
      notes: null,
    });
    setSaving(false);
    if (result.error) {
      showToast(`Save failed: ${result.error}`);
      return;
    }
    setSavedRule(draft);
    setVersion(result.version);
    invalidateBuyBoxCache();
    showToast(`Rule v${result.version} saved`);
    previewCounts(rule);
  }

  // Walk both tables in pages of 500 and PATCH any auto-graded row whose
  // stored verdict/reason no longer matches the freshly-computed one. Manual
  // rows are skipped. Returns a small summary the panel renders.
  async function handleRederive() {
    if (rederiving) return;
    setRederiving(true);
    setRederiveResult(null);

    const rule = toRule(savedRule);
    const stats = { dealsScanned: 0, dealsUpdated: 0, propsScanned: 0, propsUpdated: 0 };

    async function rederiveTable(table, selectCols, mapper, extraSets) {
      const pageSize = 500;
      let from = 0;
      while (true) {
        const { data, error } = await supabase.from(table)
          .select(selectCols)
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;

        for (const row of data) {
          if (row.buy_box_source === "manual") continue;
          const { verdict, reason } = evaluateBuyBox(rule, mapper(row));
          const changed = verdict !== row.buy_box_verdict
                      || (reason || null) !== (row.buy_box_reason || null)
                      || version !== (row.buy_box_version || 0);
          if (!changed) continue;
          const patch = {
            buy_box_verdict: verdict,
            buy_box_reason: reason,
            buy_box_source: "auto",
            buy_box_version: version,
            buy_box_updated_at: new Date().toISOString(),
            ...extraSets,
          };
          const { error: upErr } = await supabase.from(table).update(patch).eq("id", row.id);
          if (upErr) throw upErr;
          if (table === "deals") stats.dealsUpdated++;
          else stats.propsUpdated++;
        }
        if (table === "deals") stats.dealsScanned += data.length;
        else stats.propsScanned += data.length;
        if (data.length < pageSize) break;
        from += pageSize;
      }
    }

    try {
      await rederiveTable(
        "deals",
        "id, msas, market, product_type, square_footage, cap_rate, year_built, clear_height, buy_box_verdict, buy_box_reason, buy_box_source, buy_box_version",
        r => ({ msas: r.msas, market: r.market, product_type: r.product_type,
                squareFeet: r.square_footage, capRate: r.cap_rate,
                year_built: r.year_built, clear_height: r.clear_height }),
      );
      await rederiveTable(
        "properties",
        "id, msas, market, product_type, size_sf, building_sf, year_built, clear_height, buy_box_verdict, buy_box_reason, buy_box_source, buy_box_version",
        r => ({ msas: r.msas, market: r.market, product_type: r.product_type,
                size_sf: r.size_sf, building_sf: r.building_sf,
                year_built: r.year_built, clear_height: r.clear_height }),
        { updated_at: new Date().toISOString() },
      );
      setRederiveResult({ ok: true, ...stats });
      showToast(`Re-derived ${stats.dealsUpdated} deals and ${stats.propsUpdated} properties`);
    } catch (e) {
      setRederiveResult({ ok: false, error: e.message });
      showToast(`Re-derive failed: ${e.message}`);
    } finally {
      setRederiving(false);
    }
  }

  if (loading) return null;

  const labelCls = "block text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider mb-1.5";
  const inputCls = "w-full bg-[#3a3a3a] border border-[#4a4a4a] text-[#b4b4b4] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ring-[#e93529]";
  const chipCls  = (on) => `text-xs px-2.5 py-1 rounded-full border transition-colors cursor-pointer ${on
    ? "bg-pp-red text-white border-pp-red"
    : "bg-[#3a3a3a] text-[#b4b4b4] border-[#4a4a4a] hover:border-pp-red/60"}`;

  const inBox = rederiveSummary?.deals?.in_box ?? 0;
  const filtered = rederiveSummary?.deals?.filtered ?? 0;
  const undecided = rederiveSummary?.deals?.undecided ?? 0;
  const manual = rederiveSummary?.deals?.manual ?? 0;

  return (
    <div className="bg-[#2c2c2c] rounded-xl p-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)] px-4 py-2 rounded-lg text-sm shadow-lg">
          {toast}
        </div>
      )}

      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-lg font-bold text-white font-montserrat">Buy-Box Rule</h2>
        <span className="text-xs text-[#8a8a8a]">v{version}</span>
      </div>
      <p className="text-sm text-[#8a8a8a] mb-4">
        Deals and properties that fail any specified dimension are marked
        <span className="mx-1 text-[#b4b4b4]">Filtered</span>; unspecified dimensions are
        treated as "any". Saving publishes a new version and re-grades all
        auto-graded rows on next re-derive.
      </p>

      {/* ── MSA whitelist ── */}
      <div className="mb-5">
        <label className={labelCls}>Allowed MSAs</label>
        <div className="flex flex-wrap gap-1.5">
          {msaList.length === 0 && <p className="text-xs text-[#8a8a8a]">No MSAs defined yet — add them in the section above.</p>}
          {msaList.map(m => (
            <button key={m} onClick={() => toggleInList("msas", m)}
              className={chipCls(draft.msas.includes(m))}>
              {m}
            </button>
          ))}
        </div>
        {draft.msas.length === 0 && (
          <p className="text-[11px] text-[#8a8a8a] mt-1.5">None selected → any MSA is allowed.</p>
        )}
      </div>

      {/* ── Product types ── */}
      <div className="mb-5">
        <label className={labelCls}>Allowed Product Types</label>
        <div className="flex flex-wrap gap-1.5">
          {PRODUCT_TYPES.map(t => (
            <button key={t.value} onClick={() => toggleInList("product_types", t.value)}
              className={chipCls(draft.product_types.includes(t.value))}>
              {t.label}
            </button>
          ))}
        </div>
        {draft.product_types.length === 0 && (
          <p className="text-[11px] text-[#8a8a8a] mt-1.5">None selected → any product type is allowed.</p>
        )}
      </div>

      {/* ── SF band + cap rate floor ── */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <label className={labelCls}>Min SF</label>
          <input type="number" placeholder="—" value={draft.sf_min}
            onChange={e => setDraft(d => ({ ...d, sf_min: e.target.value }))}
            className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Max SF</label>
          <input type="number" placeholder="—" value={draft.sf_max}
            onChange={e => setDraft(d => ({ ...d, sf_max: e.target.value }))}
            className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Min Cap Rate (%)</label>
          <input type="number" step="0.01" placeholder="—" value={draft.cap_rate_min}
            onChange={e => setDraft(d => ({ ...d, cap_rate_min: e.target.value }))}
            className={inputCls} />
        </div>
      </div>

      {/* ── Vintage + clear height ── */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className={labelCls}>Min Year Built</label>
          <input type="number" placeholder="e.g. 1990" value={draft.year_built_min}
            onChange={e => setDraft(d => ({ ...d, year_built_min: e.target.value }))}
            className={inputCls} />
          <p className="text-[11px] text-[#8a8a8a] mt-1">Rows without a year_built value pass this dimension.</p>
        </div>
        <div>
          <label className={labelCls}>Min Clear Height (ft)</label>
          <input type="number" step="0.5" placeholder="e.g. 24" value={draft.clear_height_min}
            onChange={e => setDraft(d => ({ ...d, clear_height_min: e.target.value }))}
            className={inputCls} />
          <p className="text-[11px] text-[#8a8a8a] mt-1">Rows without a clear_height value pass this dimension.</p>
        </div>
      </div>

      {/* ── Preview counts (deals only — properties are same shape) ── */}
      {rederiveSummary && (
        <div className="bg-[rgba(36,36,36,0.6)] border border-[rgba(58,58,58,0.5)] rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider">Deal counts under saved rule</p>
            <button onClick={() => previewCounts(toRule(draft))} className="text-[10px] text-[#f04f44] hover:text-[#f04f44]">Preview draft →</button>
          </div>
          <div className="flex gap-3 text-xs">
            <span className="text-[#4f9d76] font-semibold">{inBox}</span>
            <span className="text-[#8a8a8a]">in-box</span>
            <span className="text-[#8a8a8a] font-semibold ml-2">{filtered}</span>
            <span className="text-[#8a8a8a]">filtered</span>
            <span className="text-[#8a8a8a] font-semibold ml-2">{undecided}</span>
            <span className="text-[#8a8a8a]">undecided</span>
            {manual > 0 && (
              <>
                <span className="text-[#f04f44] font-semibold ml-2">{manual}</span>
                <span className="text-[#8a8a8a]">manual override</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          style={{ color: "#fff" }}
          className="bg-pp-red hover:bg-pp-red-light text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : dirty ? "Publish new version" : `Saved (v${version})`}
        </button>
        <button
          onClick={handleRederive}
          disabled={rederiving}
          className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#f4f4f4] text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          title="Re-run the saved rule against every auto-graded deal + property"
        >
          {rederiving ? "Re-deriving…" : "Re-derive all rows"}
        </button>
      </div>
      {rederiveResult && rederiveResult.ok && (
        <p className="text-xs text-[#8a8a8a] mt-2">
          Scanned {rederiveResult.dealsScanned} deals ({rederiveResult.dealsUpdated} updated) and
          {" "}{rederiveResult.propsScanned} properties ({rederiveResult.propsUpdated} updated).
        </p>
      )}
      {rederiveResult && !rederiveResult.ok && (
        <p className="text-xs text-[#f04f44] mt-2">Re-derive failed: {rederiveResult.error}</p>
      )}
    </div>
  );
}
